import React, { useMemo } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { BrowseInput } from "components/InputStyled";
import ModalBackdrop from "components/ModalBackdrop";

export function destinationPositionId(target = {}) {
  return target.position_id || target.id || "";
}

export function destinationPositionLabel(target = {}) {
  return [target.fund_name, target.investment_type, target.cusip_or_id || "Position"]
    .filter(Boolean)
    .join(" / ");
}

export function destinationPositionShortLabel(target = {}) {
  return [target.investment_type, target.cusip_or_id || "Position"]
    .filter(Boolean)
    .join(" - ");
}

function targetSearchText(target = {}) {
  return [
    target.fund_name,
    target.investment_type,
    target.cusip_or_id,
    target.description,
    target.issuer,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function buildDestinationPositionTree(targets = [], searchText = "") {
  const query = String(searchText || "").trim().toLowerCase();
  const fundMap = new Map();

  (Array.isArray(targets) ? targets : []).forEach((target) => {
    if (query && !targetSearchText(target).includes(query)) return;
    const fundId = target.fund_id || "__no_fund";
    if (!fundMap.has(fundId)) {
      fundMap.set(fundId, {
        id: fundId,
        name: target.fund_name || "No Fund",
        positions: [],
      });
    }
    fundMap.get(fundId).positions.push(target);
  });

  return [...fundMap.values()]
    .map((fund) => ({
      ...fund,
      positions: fund.positions.sort((a, b) =>
        destinationPositionShortLabel(a).localeCompare(destinationPositionShortLabel(b))
      ),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function DestinationPositionSelector({
  valueLabel,
  placeholder = "Select target",
  loading = false,
  disabled = false,
  onBrowse,
  className = "min-w-0 flex-1",
  controlClassName = "",
  label,
  browseLabel = "Browse…",
  browseAriaLabel = "Browse target positions",
}) {
  return (
    <BrowseInput
      label={label}
      value={valueLabel}
      placeholder={placeholder}
      loading={loading}
      disabled={disabled}
      onBrowse={onBrowse}
      browseLabel={browseLabel}
      browseAriaLabel={browseAriaLabel}
      className={className}
      controlClassName={controlClassName}
    />
  );
}

export function DestinationPositionBrowser({
  isOpen,
  targets,
  selectedTargetId,
  searchText = "",
  expandedGroupIds,
  onSearchChange,
  onToggleGroup,
  onSelectTarget,
  onClose,
  title = "Select Target Position",
  description = "",
  showSearch = false,
  compact = true,
  emptyText = "No eligible target positions.",
  searchPlaceholder = "Search fund, type, CUSIP / ID...",
}) {
  const groups = useMemo(
    () => buildDestinationPositionTree(targets, searchText),
    [searchText, targets]
  );
  const hasSearch = !!String(searchText || "").trim();

  if (!isOpen) return null;

  return (
    <ModalBackdrop onClose={onClose} zIndex="z-[1100]">
      <div
        className={`mx-4 flex max-h-[78vh] w-full flex-col rounded-lg border border-border bg-[hsl(var(--background))] text-app shadow-xl ${
          compact ? "max-w-lg" : "max-w-2xl"
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={`${compact ? "px-3 py-2" : "px-4 py-3"} border-b border-border`}>
          <div className="text-sm font-semibold text-app">{title}</div>
          {description ? (
            <div className={`${compact ? "text-[11px]" : "text-xs"} mt-1 text-muted`}>
              {description}
            </div>
          ) : null}
        </div>

        {showSearch ? (
          <div className="border-b border-border p-3">
            <input
              type="text"
              value={searchText}
              onChange={(event) => onSearchChange?.(event.target.value)}
              placeholder={searchPlaceholder}
              className="input !h-8 w-full !text-sm"
              autoFocus
            />
          </div>
        ) : null}

        <div className={`${compact ? "min-h-40 p-2" : "min-h-64 p-3"} overflow-y-auto`}>
          {groups.length ? (
            <div className="space-y-1">
              {groups.map((group) => {
                const expanded = hasSearch || expandedGroupIds.has(group.id);
                return (
                  <div key={group.id}>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-2 rounded-md px-2 text-left font-semibold hover:bg-muted/20 ${
                        compact ? "py-1 text-xs" : "py-1.5 text-sm"
                      }`}
                      onClick={() => onToggleGroup(group.id)}
                    >
                      {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                      <span className="truncate">{group.name}</span>
                    </button>
                    {expanded ? (
                      <div className="ml-5 border-l border-border pl-2">
                        {group.positions.map((target) => {
                          const id = destinationPositionId(target);
                          const selected = id === selectedTargetId;
                          return (
                            <button
                              key={id}
                              type="button"
                              className={`w-full rounded-md px-2 text-left hover:bg-muted/20 ${
                                compact ? "py-1 text-xs" : "py-1.5 text-sm"
                              } ${selected ? "bg-brand/10 font-semibold text-brand" : "text-app"}`}
                              onClick={() => onSelectTarget(target)}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="truncate">{destinationPositionShortLabel(target)}</span>
                                {selected ? (
                                  <span className={compact ? "text-[10px]" : "text-xs"}>Selected</span>
                                ) : null}
                              </div>
                              {target.description ? (
                                <div className={`${compact ? "text-[10px]" : "text-xs"} truncate text-muted`}>
                                  {target.description}
                                </div>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`${compact ? "text-xs" : "text-sm"} text-muted`}>{emptyText}</div>
          )}
        </div>

        <div className={`${compact ? "px-3 py-2" : "px-4 py-3"} flex justify-end border-t border-border`}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
